<script>
import validations from './helpers/validations';
import FormInput from 'v3/components/Form/Input.vue';
import { reactive, ref, defineAsyncComponent } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { OnClickOutside } from '@vueuse/components';

import NextButton from 'dashboard/components-next/button/Button.vue';
import EmojiIcon from 'dashboard/components-next/emoji-icon-picker/EmojiIcon.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const EmojiIconPicker = defineAsyncComponent(
  () =>
    import('dashboard/components-next/emoji-icon-picker/EmojiIconPicker.vue')
);

export default {
  components: {
    NextButton,
    FormInput,
    OnClickOutside,
    EmojiIcon,
    Icon,
    EmojiIconPicker,
  },
  props: {
    onSubmit: {
      type: Function,
      default: () => {},
    },
    submitInProgress: {
      type: Boolean,
      default: false,
    },
    formData: {
      type: Object,
      default: () => {},
    },
    submitButtonText: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const formData = props.formData || {};
    const {
      description = '',
      name: title = '',
      allow_auto_assign: allowAutoAssign = true,
      icon = '',
      icon_color: iconColor = '',
    } = formData;

    const state = reactive({
      description,
      title,
      allowAutoAssign,
      icon,
      iconColor,
    });

    const isIconPickerOpen = ref(false);
    const suggestedIcons = ['💬', '🎧', '💼', '🛠️', '📦', '🤝'];

    const rules = validations;
    const v$ = useVuelidate(rules, state);
    return { state, v$, isIconPickerOpen, suggestedIcons };
  },
  methods: {
    onSelectIcon({ type, value, color }) {
      this.state.icon = value;
      this.state.iconColor = type === 'icon' ? color : '';
      this.isIconPickerOpen = false;
    },
    onColorChange(color) {
      this.state.iconColor = color;
    },
    onRemoveIcon() {
      this.state.icon = '';
      this.state.iconColor = '';
      this.isIconPickerOpen = false;
    },
    handleSubmit() {
      if (this.submitInProgress) return;
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      this.onSubmit({
        description: this.state.description,
        name: this.state.title,
        allow_auto_assign: this.state.allowAutoAssign,
        icon: this.state.icon,
        icon_color: this.state.iconColor,
      });
    },
  },
};
</script>

<template>
  <form class="m-0 grid w-full min-w-0 gap-4" @submit.prevent="handleSubmit">
    <div
      class="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(16rem,0.7fr)]"
    >
      <section class="min-w-0 space-y-3">
        <div class="rounded-lg border border-n-weak p-3">
          <h3 class="mb-1 text-sm font-semibold text-n-slate-12">
            {{ $t('TEAMS_SETTINGS.REDESIGN.IDENTITY') }}
          </h3>
          <p class="mb-3 text-xs leading-relaxed text-n-slate-11">
            {{ $t('TEAMS_SETTINGS.REDESIGN.IDENTITY_HELP') }}
          </p>
          <div class="flex flex-wrap items-center gap-2">
            <button
              v-for="emoji in suggestedIcons"
              :key="emoji"
              type="button"
              class="grid size-10 place-items-center rounded-lg border text-xl transition-colors hover:bg-n-alpha-2"
              :class="
                state.icon === emoji
                  ? 'border-n-brand bg-n-brand/5'
                  : 'border-n-weak bg-n-solid-2'
              "
              :aria-label="`${$t('TEAMS_SETTINGS.REDESIGN.CHOOSE_ICON')}: ${emoji}`"
              :aria-pressed="state.icon === emoji"
              :disabled="submitInProgress"
              @click="onSelectIcon({ type: 'emoji', value: emoji })"
            >
              {{ emoji }}
            </button>
            <OnClickOutside
              class="relative"
              @trigger="isIconPickerOpen = false"
            >
              <NextButton
                type="button"
                variant="ghost"
                color="slate"
                icon="i-hugeicons-add-01"
                :label="$t('TEAMS_SETTINGS.REDESIGN.MORE_ICONS')"
                :disabled="submitInProgress"
                @click="isIconPickerOpen = !isIconPickerOpen"
              />
              <EmojiIconPicker
                v-if="isIconPickerOpen"
                class="start-0 top-12"
                :value="state.icon"
                :color="state.iconColor"
                show-remove-button
                @select="onSelectIcon"
                @color-change="onColorChange"
                @remove="onRemoveIcon"
              />
            </OnClickOutside>
          </div>
        </div>
        <FormInput
          v-model="state.title"
          name="title"
          spacing="compact"
          :label="$t('TEAMS_SETTINGS.FORM.NAME.LABEL')"
          :placeholder="$t('TEAMS_SETTINGS.FORM.NAME.PLACEHOLDER')"
          :has-error="v$.title.$error"
          :error-message="v$.title.$error ? v$.title.$errors[0].$message : ''"
          :disabled="submitInProgress"
          @blur="v$.title.$touch"
        />
        <label class="block text-sm font-medium text-n-slate-12">
          {{ $t('TEAMS_SETTINGS.FORM.DESCRIPTION.LABEL') }}
          <textarea
            v-model="state.description"
            rows="2"
            class="mb-0 mt-2 block w-full resize-y rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2.5 text-sm font-normal text-n-slate-12 placeholder:text-n-slate-10 focus:border-n-brand focus:outline-none"
            :placeholder="$t('TEAMS_SETTINGS.FORM.DESCRIPTION.PLACEHOLDER')"
            :disabled="submitInProgress"
          />
        </label>
      </section>
      <section class="rounded-lg border border-n-weak p-4">
        <div
          class="mb-2 grid size-7 place-items-center rounded-lg border border-n-weak"
        >
          <Icon icon="i-hugeicons-shuffle" class="size-5 text-n-slate-11" />
        </div>
        <h3 class="mb-1 text-sm font-semibold text-n-slate-12">
          {{ $t('TEAMS_SETTINGS.REDESIGN.ROUTING') }}
        </h3>
        <p class="mb-3 text-xs leading-relaxed text-n-slate-11">
          {{ $t('TEAMS_SETTINGS.REDESIGN.ROUTING_HELP') }}
        </p>
        <div class="space-y-2">
          <label
            v-for="automatic in [true, false]"
            :key="String(automatic)"
            class="flex cursor-pointer items-start gap-3 rounded-xl border p-3"
            :class="
              state.allowAutoAssign === automatic
                ? 'border-n-brand/30 bg-n-brand/5'
                : 'border-n-weak'
            "
          >
            <input
              v-model="state.allowAutoAssign"
              type="radio"
              name="team-routing"
              :value="automatic"
              :disabled="submitInProgress"
              class="mt-0.5 shrink-0"
            />
            <span
              ><strong class="block text-sm font-medium text-n-slate-12">{{
                $t(
                  automatic
                    ? 'TEAMS_SETTINGS.REDESIGN.AUTOMATIC'
                    : 'TEAMS_SETTINGS.REDESIGN.MANUAL'
                )
              }}</strong
              ><span
                class="mt-1 block text-xs leading-relaxed text-n-slate-11"
                >{{
                  $t(
                    automatic
                      ? 'TEAMS_SETTINGS.REDESIGN.AUTO_HELP'
                      : 'TEAMS_SETTINGS.REDESIGN.MANUAL_HELP'
                  )
                }}</span
              ></span
            >
          </label>
        </div>
      </section>
    </div>
    <footer
      class="flex flex-wrap items-center justify-between gap-4 border-t border-n-weak pt-4"
    >
      <div class="flex min-w-0 items-center gap-3">
        <span
          class="grid size-10 shrink-0 place-items-center rounded-xl border border-n-weak bg-n-alpha-1"
          ><EmojiIcon
            v-if="state.icon"
            :value="state.icon"
            :color="state.iconColor"
            class="size-5" /><Icon
            v-else
            icon="i-hugeicons-user-group"
            class="size-5 text-n-slate-11"
        /></span>
        <div class="min-w-0">
          <span class="block text-[11px] text-n-slate-11">{{
            $t('TEAMS_SETTINGS.REDESIGN.PREVIEW')
          }}</span
          ><strong
            class="block max-w-64 truncate text-sm font-medium text-n-slate-12"
            >{{
              state.title || $t('TEAMS_SETTINGS.REDESIGN.TEAM_NAME')
            }}</strong
          >
        </div>
      </div>
      <NextButton
        type="submit"
        icon="i-hugeicons-arrow-right-02"
        trailing-icon
        :label="submitButtonText"
        :disabled="v$.title.$invalid || submitInProgress"
        :is-loading="submitInProgress"
      />
    </footer>
  </form>
</template>
